import { inject, Injectable, signal } from '@angular/core';
import {
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
} from '@core/constants/pagination.const';
import { http_toParams } from '@core/utils/https.util';
import { FilterModel } from '@shared/models/filter.model';
import { tap } from 'rxjs';
import { APP_API_ENDPOINTS } from '../constants/app-api.const';
import { NotificationItem } from '../models/notification.model';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly apiService = inject(ApiService);
  private readonly _items = signal<NotificationItem[]>([]);

  readonly items = this._items.asReadonly();
  readonly unreadCount = signal(0);

  readonly filter = signal<FilterModel>({
    page: DEFAULT_PAGE,
    page_size: DEFAULT_PAGE_SIZE,
  });
  readonly hasMore = signal(true);
  readonly isLoadingMore = signal(false);

  loadNotifications(filter: FilterModel, append: boolean = false) {
    return this.apiService
      .get<
        NotificationItem[]
      >(APP_API_ENDPOINTS.CORE_NOTIFICATIONS.BASE, { ...http_toParams(filter as Record<string, unknown>) })
      .pipe(
        tap((res) => {
          const items = res.data ?? [];
          if (append) {
            this._items.update((prev) => {
              const existingIds = new Set(prev.map((i) => i.id));
              const uniqueNewItems = items.filter(
                (i) => !existingIds.has(i.id),
              );
              return [...prev, ...uniqueNewItems];
            });
          } else {
            this._items.set(items);
          }

          const hasNext = res.pagination?.has_next ?? false;
          this.hasMore.set(hasNext);
        }),
      );
  }

  loadInitial() {
    return this.loadNotifications(this.filter());
  }

  reset() {
    this.filter.set({ page: DEFAULT_PAGE, page_size: DEFAULT_PAGE_SIZE });
    this._items.set([]);
  }

  loadNextPage() {
    this.isLoadingMore.set(true);
    const nextPage = (this.filter().page ?? 1) + 1;
    this.filter.update((f) => ({ ...f, page: nextPage }));
    return this.loadNotifications(this.filter(), true).pipe(
      tap({
        next: () => this.isLoadingMore.set(false),
        error: () => this.isLoadingMore.set(false),
      }),
    );
  }

  loadUnreadCount() {
    return this.apiService
      .get<{
        count_unread_notifications: number;
      }>(APP_API_ENDPOINTS.CORE_NOTIFICATIONS.COUNT_UNREAD)
      .pipe(
        tap((res) => {
          this.unreadCount.set(res.data?.count_unread_notifications ?? 0);
        }),
      );
  }

  markAsRead(id: number) {
    return this.apiService
      .post(APP_API_ENDPOINTS.CORE_NOTIFICATIONS.MARK_AS_READ(id), {})
      .pipe(
        tap(() => {
          const currentItem = this._items().find((item) => item.id === id);

          this._items.update((items) =>
            items.map((item) =>
              item.id === id && !item.is_read
                ? { ...item, is_read: true }
                : item,
            ),
          );

          if (currentItem && !currentItem.is_read) {
            this.unreadCount.update((count) => Math.max(0, count - 1));
          }
        }),
      );
  }

  markAllAsRead() {
    return this.apiService
      .post(APP_API_ENDPOINTS.CORE_NOTIFICATIONS.MARK_ALL_AS_READ, {})
      .pipe(
        tap(() => {
          this._items.update((items) =>
            items.map((item) => ({ ...item, is_read: true })),
          );
          this.unreadCount.set(0);
        }),
      );
  }
}
